import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import * as portfolioService from '../services/portfolioService';
import * as artworkService from '../services/artworkService';
import { parseError } from '../utils/errorMessages';

const PortfolioContext = createContext(null);

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) throw new Error('usePortfolio must be used within PortfolioProvider');
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [insuranceData, setInsuranceData] = useState(null);

  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await portfolioService.getPortfolios(authenticatedFetch);
      setPortfolios(data);
    } catch (err) {
      setError(parseError(err).message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchPortfolioDetail = useCallback(async (portfolioId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await portfolioService.getPortfolioDetail(authenticatedFetch, portfolioId);
      setCurrentPortfolio(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const createNewPortfolio = useCallback(async (name, description) => {
    setError(null);
    try {
      const data = await portfolioService.createPortfolio(authenticatedFetch, { name, description });
      setPortfolios(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch]);

  const updateExistingPortfolio = useCallback(async (portfolioId, name, description) => {
    setError(null);
    try {
      const data = await portfolioService.updatePortfolio(authenticatedFetch, portfolioId, { name, description });
      setPortfolios(prev => prev.map(p => p.id === portfolioId ? data : p));
      if (currentPortfolio?.id === portfolioId) {
        setCurrentPortfolio(prev => ({ ...prev, ...data }));
      }
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentPortfolio?.id]);

  const deleteExistingPortfolio = useCallback(async (portfolioId) => {
    setError(null);
    try {
      await portfolioService.deletePortfolio(authenticatedFetch, portfolioId);
      setPortfolios(prev => prev.filter(p => p.id !== portfolioId));
      if (currentPortfolio?.id === portfolioId) {
        setCurrentPortfolio(null);
        setAnalytics(null);
        setChartData(null);
        setInsights(null);
      }
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentPortfolio?.id]);

  const addNewHolding = useCallback(async (portfolioId, holdingData) => {
    setError(null);
    try {
      const data = await portfolioService.addHolding(authenticatedFetch, portfolioId, holdingData);
      if (currentPortfolio?.id === portfolioId) {
        setCurrentPortfolio(prev => ({
          ...prev,
          holdings: [...(prev.holdings || []), data],
        }));
      }
      // Clear analytics cache since holdings changed
      setAnalytics(null);
      setChartData(null);
      setInsights(null);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentPortfolio?.id]);

  const removeExistingHolding = useCallback(async (portfolioId, holdingId) => {
    setError(null);
    try {
      await portfolioService.removeHolding(authenticatedFetch, portfolioId, holdingId);
      if (currentPortfolio?.id === portfolioId) {
        setCurrentPortfolio(prev => ({
          ...prev,
          holdings: (prev.holdings || []).filter(h => h.id !== holdingId),
        }));
      }
      // Clear analytics and insurance cache since holdings changed
      setAnalytics(null);
      setChartData(null);
      setInsights(null);
      setInsuranceData(null);
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentPortfolio?.id]);

  const updateExistingHolding = useCallback(async (portfolioId, holdingId, data) => {
    setError(null);
    try {
      const updated = await portfolioService.updateHolding(authenticatedFetch, portfolioId, holdingId, data);
      if (currentPortfolio?.id === portfolioId) {
        setCurrentPortfolio(prev => ({
          ...prev,
          holdings: (prev.holdings || []).map(h => h.id === holdingId ? updated : h),
        }));
      }
      // Clear insurance cache since insurance fields may have changed
      setInsuranceData(null);
      return updated;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentPortfolio?.id]);

  const fetchInsuranceData = useCallback(async (portfolioId) => {
    try {
      const data = await portfolioService.getPortfolioInsurance(authenticatedFetch, portfolioId);
      setInsuranceData(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    }
  }, [authenticatedFetch]);

  const searchArtworks = useCallback(async (query, limit = 20) => {
    return artworkService.searchArtworks(authenticatedFetch, query, limit);
  }, [authenticatedFetch]);

  const createArtwork = useCallback(async (artworkData) => {
    return artworkService.createArtwork(authenticatedFetch, artworkData);
  }, [authenticatedFetch]);

  // --- Analytics Methods ---

  const fetchPortfolioAnalytics = useCallback(async (portfolioId) => {
    setAnalyticsLoading(true);
    try {
      const data = await portfolioService.getPortfolioAnalytics(authenticatedFetch, portfolioId);
      setAnalytics(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setAnalyticsLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchChartData = useCallback(async (portfolioId, timeframe = '1Y') => {
    try {
      const data = await portfolioService.getPortfolioChart(authenticatedFetch, portfolioId, timeframe);
      setChartData(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    }
  }, [authenticatedFetch]);

  const fetchInsights = useCallback(async (portfolioId) => {
    try {
      const data = await portfolioService.getPortfolioInsights(authenticatedFetch, portfolioId);
      setInsights(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    }
  }, [authenticatedFetch]);

  const triggerRevalue = useCallback(async (portfolioId) => {
    setAnalyticsLoading(true);
    try {
      const result = await portfolioService.revaluePortfolio(authenticatedFetch, portfolioId);
      // Refresh analytics after revaluation
      setAnalytics(null);
      setChartData(null);
      setInsights(null);
      return result;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    } finally {
      setAnalyticsLoading(false);
    }
  }, [authenticatedFetch]);

  const value = {
    portfolios,
    currentPortfolio,
    loading,
    error,
    analytics,
    chartData,
    insights,
    analyticsLoading,
    insuranceData,
    fetchPortfolios,
    fetchPortfolioDetail,
    createNewPortfolio,
    updateExistingPortfolio,
    deleteExistingPortfolio,
    addNewHolding,
    removeExistingHolding,
    updateExistingHolding,
    searchArtworks,
    createArtwork,
    fetchPortfolioAnalytics,
    fetchChartData,
    fetchInsights,
    fetchInsuranceData,
    triggerRevalue,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};
